/**
 * OmniLeagues — Bridge Sync Queue Introspection
 * Surfaces the per-tenant `replication_queue` and per-entity wp_sync_state
 * counts for the Tech Specialist / Athletic Director.
 *
 * Endpoints used:
 *   GET  /api/sync/tenant/{org}/queue
 *   GET  /api/sync/tenant/{org}/status
 *   POST /api/sync/tenant/{org}/queue/{id}/retry
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import BridgeReconcileCard from './BridgeReconcileCard';
import {
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, Clock,
  PlayCircle, RotateCw, Activity,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTs(value) {
  if (!value) return '—';
  if (typeof value === 'number') {
    try { return new Date(value).toLocaleString(); } catch { return String(value); }
  }
  try { return new Date(value).toLocaleString(); } catch { return String(value); }
}

function StatusPill({ status, count, isDark }) {
  const palette = {
    pending: ['bg-amber-500/15', 'text-amber-400', 'border-amber-700/40'],
    in_flight: ['bg-blue-500/15', 'text-blue-400', 'border-blue-700/40'],
    done: ['bg-emerald-500/15', 'text-emerald-400', 'border-emerald-700/40'],
    error: ['bg-orange-500/15', 'text-orange-400', 'border-orange-700/40'],
    dead: ['bg-red-500/15', 'text-red-400', 'border-red-700/40'],
  }[status] || ['bg-slate-500/15', 'text-slate-300', 'border-slate-700/40'];
  return (
    <div
      data-testid={`sync-pill-${status}`}
      className={`rounded-lg px-3 py-2 border ${palette[0]} ${palette[2]} flex flex-col items-start`}
    >
      <span className={`text-[11px] uppercase tracking-wide font-semibold ${palette[1]}`}>{status.replace('_', ' ')}</span>
      <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{count}</span>
    </div>
  );
}

export default function SyncQueueSection({ orgId }) {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState(null);
  const [status, setStatus] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const text = isDark ? 'text-slate-100' : 'text-slate-900';
  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';

  const fetchAll = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/api/sync/tenant/${orgId}/queue`, { withCredentials: true }),
        axios.get(`${API}/api/sync/tenant/${orgId}/status`, { withCredentials: true }),
      ]);
      setQueue(q.data?.data || null);
      setStatus(s.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load sync data');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(fetchAll, 10000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchAll]);

  const handleRetry = async (queueId) => {
    setRetrying(queueId);
    try {
      await axios.post(`${API}/api/sync/tenant/${orgId}/queue/${queueId}/retry`, {}, { withCredentials: true });
      await fetchAll();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  const byStatus = queue?.by_status || {};
  const total = useMemo(
    () => Object.values(byStatus).reduce((a, b) => a + (Number(b) || 0), 0),
    [byStatus],
  );

  const statusRows = useMemo(() => {
    if (!status) return [];
    return Object.entries(status)
      .map(([entity, v]) => ({ entity, ...v }))
      .filter(r => (r.total || 0) > 0)
      .sort((a, b) => (b.pending + b.error + b.drift) - (a.pending + a.error + a.drift));
  }, [status]);

  return (
    <div className="space-y-4" data-testid="sync-queue-section">
      <Card className={card}>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4 text-teal-400" />
                Bridge Sync Queue
              </CardTitle>
              <p className={`text-xs ${muted} mt-1`}>
                Every OL → WordPress write goes through the <code>replication_queue</code>.
                Pending rows are about to ship · errors will auto-retry · dead rows need manual review.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={autoRefresh ? 'default' : 'outline'}
                onClick={() => setAutoRefresh(v => !v)}
                className={autoRefresh ? 'bg-teal-600 hover:bg-teal-700 h-8 text-xs' : 'h-8 text-xs'}
                data-testid="sync-auto-refresh-btn"
              >
                <Clock className="h-3 w-3 mr-1" />
                {autoRefresh ? 'Auto · 10s' : 'Auto-refresh off'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchAll}
                disabled={loading}
                className="h-8 text-xs"
                data-testid="sync-refresh-btn"
              >
                {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-700/40 text-red-400 text-xs"
                 data-testid="sync-error-banner">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="flex-1">{error}</div>
            </div>
          )}

          {/* Status pill grid */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <StatusPill status="pending" count={byStatus.pending || 0} isDark={isDark} />
            <StatusPill status="in_flight" count={byStatus.in_flight || 0} isDark={isDark} />
            <StatusPill status="done" count={byStatus.done || 0} isDark={isDark} />
            <StatusPill status="error" count={byStatus.error || 0} isDark={isDark} />
            <StatusPill status="dead" count={byStatus.dead || 0} isDark={isDark} />
          </div>

          <div className={`text-xs ${muted}`}>
            Total queued rows for this org: <span className={`font-semibold ${text}`}>{total}</span>
          </div>

          {/* Oldest pending */}
          {queue?.oldest_pending && (
            <Card className={`${isDark ? 'bg-amber-900/15 border-amber-700/40' : 'bg-amber-50 border-amber-200'}`}>
              <CardContent className="p-3 text-xs flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-400" />
                  <span className={text}>
                    <strong>Oldest pending</strong> · {queue.oldest_pending.entity} ·
                    {' '}<code className="text-[11px]">{queue.oldest_pending.id?.slice(0, 8)}</code> ·
                    attempts {queue.oldest_pending.attempts || 0} ·
                    {' '}next: {fmtTs(queue.oldest_pending.next_attempt_at)}
                  </span>
                </div>
                {queue.oldest_pending.last_error && (
                  <Badge variant="outline" className="text-[10px] text-orange-400 border-orange-700/40">
                    last error
                  </Badge>
                )}
              </CardContent>
            </Card>
          )}

          {/* In-flight rows */}
          {queue?.in_flight?.length > 0 && (
            <div>
              <div className={`text-xs font-semibold mb-1 ${text}`}>In-flight</div>
              <ul className="space-y-1" data-testid="sync-inflight-list">
                {queue.in_flight.map(r => (
                  <li key={r.id} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${isDark ? 'bg-slate-900/40' : 'bg-slate-50'}`}>
                    <span className="flex items-center gap-2">
                      <PlayCircle className="h-3.5 w-3.5 text-blue-400" />
                      <span className={text}>{r.entity}</span>
                      <code className={`text-[10px] ${muted}`}>{r.id.slice(0, 8)}</code>
                      <Badge variant="outline" className="text-[10px]">{r.method}</Badge>
                    </span>
                    <span className={`text-[10px] ${muted}`}>{fmtTs(r.started_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recent errors */}
          {queue?.recent_errors?.length > 0 && (
            <div>
              <div className={`text-xs font-semibold mb-1 ${text}`}>Recent errors (will auto-retry)</div>
              <ul className="space-y-1" data-testid="sync-error-list">
                {queue.recent_errors.map(r => (
                  <li key={r.id}
                      className={`flex items-start justify-between gap-2 text-xs px-2 py-1.5 rounded border ${isDark ? 'bg-orange-900/10 border-orange-700/30' : 'bg-orange-50 border-orange-200'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-orange-400 shrink-0" />
                        <span className={text}>{r.entity}</span>
                        <code className={`text-[10px] ${muted}`}>{r.id.slice(0, 8)}</code>
                        <Badge variant="outline" className="text-[10px]">{r.method}</Badge>
                        <Badge variant="outline" className="text-[10px]">attempts {r.attempts}</Badge>
                      </div>
                      {r.last_error && (
                        <div className={`mt-0.5 text-[11px] ${muted} truncate`}>
                          {typeof r.last_error === 'string'
                            ? r.last_error
                            : (r.last_error.message || r.last_error.code || JSON.stringify(r.last_error))}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRetry(r.id)}
                      disabled={retrying === r.id}
                      className="h-6 text-[11px] shrink-0"
                      data-testid={`sync-retry-${r.id}`}
                    >
                      {retrying === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3 mr-1" />}
                      Retry
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Dead letters */}
          {queue?.dead_letters?.length > 0 && (
            <div>
              <div className={`text-xs font-semibold mb-1 ${text}`}>Dead letters (manual review needed)</div>
              <ul className="space-y-1" data-testid="sync-dead-list">
                {queue.dead_letters.map(r => (
                  <li key={r.id}
                      className={`flex items-start justify-between gap-2 text-xs px-2 py-1.5 rounded border ${isDark ? 'bg-red-900/15 border-red-700/40' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={text}>{r.entity}</span>
                        <code className={`text-[10px] ${muted}`}>{r.id.slice(0, 8)}</code>
                        <Badge variant="outline" className="text-[10px]">{r.method} {r.path}</Badge>
                        <Badge variant="outline" className="text-[10px]">attempts {r.attempts}</Badge>
                        <span className={`text-[10px] ${muted}`}>{fmtTs(r.completed_at)}</span>
                      </div>
                      {r.last_error && (
                        <div className={`mt-0.5 text-[11px] ${muted} truncate`}>
                          {typeof r.last_error === 'string'
                            ? r.last_error
                            : (r.last_error.message || r.last_error.code || JSON.stringify(r.last_error))}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRetry(r.id)}
                      disabled={retrying === r.id}
                      className="h-6 text-[11px] shrink-0"
                      data-testid={`sync-revive-${r.id}`}
                    >
                      {retrying === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3 mr-1" />}
                      Revive
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {total === 0 && !error && (
            <div className={`flex items-center gap-2 p-3 rounded-md text-xs ${isDark ? 'bg-emerald-900/15 text-emerald-300 border border-emerald-700/40' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
              <CheckCircle2 className="h-4 w-4" />
              Queue is empty. Every OL write has been confirmed on WordPress.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-entity sync state */}
      <Card className={card}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="h-4 w-4 text-teal-400" />
            Per-entity sync state
          </CardTitle>
          <p className={`text-xs ${muted} mt-1`}>
            How many of each collection's docs are <code>synced</code> vs <code>pending</code> /
            <code> error</code> / <code>drift</code>. Collections with zero pending+error are healthy.
          </p>
        </CardHeader>
        <CardContent>
          {statusRows.length === 0 ? (
            <div className={`text-xs ${muted}`}>No sync-tracked collections have data for this org yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  <tr>
                    <th className="text-left py-1.5 px-2">Entity</th>
                    <th className="text-right py-1.5 px-2">Total</th>
                    <th className="text-right py-1.5 px-2">Synced</th>
                    <th className="text-right py-1.5 px-2">Pending</th>
                    <th className="text-right py-1.5 px-2">Error</th>
                    <th className="text-right py-1.5 px-2">Drift</th>
                  </tr>
                </thead>
                <tbody>
                  {statusRows.map(r => {
                    const healthy = (r.pending || 0) + (r.error || 0) + (r.drift || 0) === 0;
                    return (
                      <tr key={r.entity} className={`${isDark ? 'border-slate-700/50' : 'border-slate-200'} border-t`}>
                        <td className={`py-1.5 px-2 ${text}`}>
                          <span className="inline-flex items-center gap-1.5">
                            {healthy
                              ? <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                              : <AlertTriangle className="h-3 w-3 text-amber-400" />}
                            {r.entity}
                          </span>
                        </td>
                        <td className={`py-1.5 px-2 text-right ${text}`}>{r.total || 0}</td>
                        <td className={`py-1.5 px-2 text-right ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{r.synced || 0}</td>
                        <td className={`py-1.5 px-2 text-right ${(r.pending || 0) > 0 ? 'text-amber-400' : muted}`}>{r.pending || 0}</td>
                        <td className={`py-1.5 px-2 text-right ${(r.error || 0) > 0 ? 'text-orange-400' : muted}`}>{r.error || 0}</td>
                        <td className={`py-1.5 px-2 text-right ${(r.drift || 0) > 0 ? 'text-red-400' : muted}`}>{r.drift || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <BridgeReconcileCard orgId={orgId} />
    </div>
  );
}
